"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ContentDeliveryLayerNode = exports.DEFAULT_ACCEPT_PROXY_CONNECTIONS = exports.DEFAULT_NEIGHBOR_TARGET_COUNT = exports.DEFAULT_NODE_VIEW_SIZE = void 0;
const dht_1 = require("@streamr/dht");
const utils_1 = require("@streamr/utils");
const eventemitter3_1 = require("eventemitter3");
const NetworkRpc_1 = require("../../generated/packages/trackerless-network/protos/NetworkRpc");
const NetworkRpc_client_1 = require("../../generated/packages/trackerless-network/protos/NetworkRpc.client");
const ContentDeliveryRpcLocal_1 = require("./ContentDeliveryRpcLocal");
const ContentDeliveryRpcRemote_1 = require("./ContentDeliveryRpcRemote");
const utils_2 = require("./utils");
exports.DEFAULT_NODE_VIEW_SIZE = 20;
exports.DEFAULT_NEIGHBOR_TARGET_COUNT = 4;
exports.DEFAULT_ACCEPT_PROXY_CONNECTIONS = false;
const logger = new utils_1.Logger(module);
class ContentDeliveryLayerNode extends eventemitter3_1.EventEmitter {
    started = false;
    duplicateDetectors;
    options;
    contentDeliveryRpcLocal;
    abortController = new AbortController();
    messagesPropagated = 0;
    constructor(options) {
        super();
        this.options = options;
        this.duplicateDetectors = new Map();
        this.contentDeliveryRpcLocal = new ContentDeliveryRpcLocal_1.ContentDeliveryRpcLocal({
            localPeerDescriptor: this.options.localPeerDescriptor,
            streamPartId: this.options.streamPartId,
            rpcCommunicator: this.options.rpcCommunicator,
            markAndCheckDuplicate: (msg, prev) => (0, utils_2.markAndCheckDuplicate)(this.duplicateDetectors, msg, prev),
            broadcast: (message, previousNode) => this.broadcast(message, previousNode),
            onLeaveNotice: (remoteNodeId, sourceIsStreamEntryPoint) => {
                if (this.abortController.signal.aborted) {
                    return;
                }
                const contact = this.options.nearbyNodeView.get(remoteNodeId)
                    ?? this.options.randomNodeView.get(remoteNodeId)
                    ?? this.options.neighbors.get(remoteNodeId)
                    ?? this.options.proxyConnectionRpcLocal?.getConnection(remoteNodeId)?.remote;
                // TODO: check integrity of notifier?
                if (contact) {
                    this.options.discoveryLayerNode.removeContact(remoteNodeId);
                    this.options.neighbors.remove(remoteNodeId);
                    this.options.nearbyNodeView.remove(remoteNodeId);
                    this.options.randomNodeView.remove(remoteNodeId);
                    this.options.leftNodeView.remove(remoteNodeId);
                    this.options.rightNodeView.remove(remoteNodeId);
                    this.options.neighborFinder.start([remoteNodeId]);
                    this.options.proxyConnectionRpcLocal?.removeConnection(remoteNodeId);
                }
                if (sourceIsStreamEntryPoint) {
                    this.emit('entryPointLeaveDetected');
                }
            },
            markForInspection: (remoteNodeId, messageId) => this.options.inspector.markMessage(remoteNodeId, messageId),
            plumTreeManager: this.options.plumTreeManager
        });
    }
    async start() {
        this.started = true;
        this.registerDefaultServerMethods();
        (0, utils_1.addManagedEventListener)(this.options.discoveryLayerNode, 'nearbyContactAdded', () => this.onNearbyContactAdded(), this.abortController.signal);
        (0, utils_1.addManagedEventListener)(this.options.discoveryLayerNode, 'nearbyContactRemoved', () => this.onNearbyContactRemoved(), this.abortController.signal);
        (0, utils_1.addManagedEventListener)(this.options.discoveryLayerNode, 'randomContactAdded', () => this.onRandomContactAdded(), this.abortController.signal);
        (0, utils_1.addManagedEventListener)(this.options.discoveryLayerNode, 'randomContactRemoved', () => this.onRandomContactRemoved(), this.abortController.signal);
        (0, utils_1.addManagedEventListener)(this.options.discoveryLayerNode, 'ringContactAdded', () => this.onRingContactsUpdated(), this.abortController.signal);
        (0, utils_1.addManagedEventListener)(this.options.discoveryLayerNode, 'ringContactRemoved', () => this.onRingContactsUpdated(), this.abortController.signal);
        (0, utils_1.addManagedEventListener)(this.options.transport, 'disconnected', (peerDescriptor) => this.onNodeDisconnected(peerDescriptor), this.abortController.signal);
        (0, utils_1.addManagedEventListener)(this.options.neighbors, 'nodeAdded', (id, remote) => {
            this.options.propagation.onNeighborJoined(id);
            this.options.connectionLocker.weakLockConnection((0, dht_1.toNodeId)(remote.getPeerDescriptor()), this.options.streamPartId);
            this.emit('neighborConnected', id);
        }, this.abortController.signal);
        (0, utils_1.addManagedEventListener)(this.options.neighbors, 'nodeRemoved', (_id, remote) => {
            this.options.connectionLocker.weakUnlockConnection((0, dht_1.toNodeId)(remote.getPeerDescriptor()), this.options.streamPartId);
        }, this.abortController.signal);
        if (this.options.proxyConnectionRpcLocal !== undefined) {
            (0, utils_1.addManagedEventListener)(this.options.proxyConnectionRpcLocal, 'newConnection', (id) => this.options.propagation.onNeighborJoined(id), this.abortController.signal);
        }
        if (this.options.plumTreeManager) {
            (0, utils_1.addManagedEventListener)(this.options.plumTreeManager, 'message', (msg) => this.emit('message', msg), this.abortController.signal);
        }
        this.options.neighborFinder.start();
        await this.options.neighborUpdateManager.start();
    }
    registerDefaultServerMethods() {
        this.options.rpcCommunicator.registerRpcNotification(NetworkRpc_1.StreamMessage, 'sendStreamMessage', (msg, context) => this.contentDeliveryRpcLocal.sendStreamMessage(msg, context));
        this.options.rpcCommunicator.registerRpcNotification(NetworkRpc_1.LeaveStreamPartNotice, 'leaveStreamPartNotice', (req, context) => this.contentDeliveryRpcLocal.leaveStreamPartNotice(req, context));
        this.options.rpcCommunicator.registerRpcMethod(NetworkRpc_1.TemporaryConnectionRequest, NetworkRpc_1.TemporaryConnectionResponse, 'openConnection', (req, context) => this.options.temporaryConnectionRpcLocal.openConnection(req, context));
        this.options.rpcCommunicator.registerRpcNotification(NetworkRpc_1.CloseTemporaryConnection, 'closeConnection', (req, context) => this.options.temporaryConnectionRpcLocal.closeConnection(req, context));
    }
    onRingContactsUpdated() {
        logger.trace('onRingContactsUpdated');
        if (this.isStopped()) {
            return;
        }
        const contacts = this.options.discoveryLayerNode.getRingContacts();
        this.options.leftNodeView.replaceAll(contacts.left.map((peer) => new ContentDeliveryRpcRemote_1.ContentDeliveryRpcRemote(this.options.localPeerDescriptor, peer, this.options.rpcCommunicator, NetworkRpc_client_1.ContentDeliveryRpcClient, this.options.rpcRequestTimeout)));
        this.options.rightNodeView.replaceAll(contacts.right.map((peer) => new ContentDeliveryRpcRemote_1.ContentDeliveryRpcRemote(this.options.localPeerDescriptor, peer, this.options.rpcCommunicator, NetworkRpc_client_1.ContentDeliveryRpcClient, this.options.rpcRequestTimeout)));
    }
    onNearbyContactAdded() {
        logger.trace(`New nearby contact found`);
        if (this.isStopped()) {
            return;
        }
        const closestContacts = this.options.discoveryLayerNode.getClosestContacts();
        this.updateNearbyNodeView(closestContacts);
        if (this.options.neighbors.size() < this.options.neighborTargetCount) {
            this.options.neighborFinder.start();
        }
    }
    onNearbyContactRemoved() {
        logger.trace(`Nearby contact removed`);
        if (this.isStopped()) {
            return;
        }
        const closestContacts = this.options.discoveryLayerNode.getClosestContacts();
        this.updateNearbyNodeView(closestContacts);
    }
    updateNearbyNodeView(nodes) {
        this.options.nearbyNodeView.replaceAll(Array.from(nodes).map((descriptor) => new ContentDeliveryRpcRemote_1.ContentDeliveryRpcRemote(this.options.localPeerDescriptor, descriptor, this.options.rpcCommunicator, NetworkRpc_client_1.ContentDeliveryRpcClient, this.options.rpcRequestTimeout)));
        for (const descriptor of this.options.discoveryLayerNode.getNeighbors()) {
            if (this.options.nearbyNodeView.size() >= this.options.nodeViewSize) {
                break;
            }
            this.options.nearbyNodeView.add(new ContentDeliveryRpcRemote_1.ContentDeliveryRpcRemote(this.options.localPeerDescriptor, descriptor, this.options.rpcCommunicator, NetworkRpc_client_1.ContentDeliveryRpcClient, this.options.rpcRequestTimeout));
        }
    }
    onRandomContactAdded() {
        if (this.isStopped()) {
            return;
        }
        const randomContacts = this.options.discoveryLayerNode.getRandomContacts(this.options.nodeViewSize);
        this.options.randomNodeView.replaceAll(randomContacts.map((descriptor) => new ContentDeliveryRpcRemote_1.ContentDeliveryRpcRemote(this.options.localPeerDescriptor, descriptor, this.options.rpcCommunicator, NetworkRpc_client_1.ContentDeliveryRpcClient, this.options.rpcRequestTimeout)));
        if (this.options.neighbors.size() < this.options.neighborTargetCount) {
            this.options.neighborFinder.start();
        }
    }
    onRandomContactRemoved() {
        logger.trace(`New random contact removed`);
        if (this.isStopped()) {
            return;
        }
        const randomContacts = this.options.discoveryLayerNode.getRandomContacts(this.options.nodeViewSize);
        this.options.randomNodeView.replaceAll(randomContacts.map((descriptor) => new ContentDeliveryRpcRemote_1.ContentDeliveryRpcRemote(this.options.localPeerDescriptor, descriptor, this.options.rpcCommunicator, NetworkRpc_client_1.ContentDeliveryRpcClient, this.options.rpcRequestTimeout)));
    }
    onNodeDisconnected(peerDescriptor) {
        const nodeId = (0, dht_1.toNodeId)(peerDescriptor);
        if (this.options.neighbors.has(nodeId)) {
            this.options.neighbors.remove(nodeId);
            this.options.neighborFinder.start([nodeId]);
            this.options.temporaryConnectionRpcLocal.removeNode(nodeId);
        }
    }
    hasProxyConnection(nodeId) {
        if (this.options.proxyConnectionRpcLocal) {
            return this.options.proxyConnectionRpcLocal.hasConnection(nodeId);
        }
        return false;
    }
    stop() {
        if (!this.started) {
            return;
        }
        this.abortController.abort();
        this.options.proxyConnectionRpcLocal?.stop();
        this.options.neighbors.getAll().map((remote) => {
            remote.leaveStreamPartNotice(this.options.streamPartId, this.options.isLocalNodeEntryPoint());
            this.options.connectionLocker.weakUnlockConnection((0, dht_1.toNodeId)(remote.getPeerDescriptor()), this.options.streamPartId);
        });
        this.options.rpcCommunicator.destroy();
        this.removeAllListeners();
        this.options.plumTreeManager?.stop();
        this.options.nearbyNodeView.stop();
        this.options.neighbors.stop();
        this.options.randomNodeView.stop();
        this.options.neighborFinder.stop();
        this.options.neighborUpdateManager.stop();
        this.options.inspector.stop();
    }
    broadcast(msg, previousNode) {
        if (!previousNode) {
            (0, utils_2.markAndCheckDuplicate)(this.duplicateDetectors, msg.messageId, msg.previousMessageRef);
        }
        this.emit('message', msg);
        const skipBackPropagation = previousNode !== undefined && !this.options.temporaryConnectionRpcLocal.hasNode(previousNode);
        this.options.propagation.feedUnseenMessage(msg, this.getPropagationTargets(msg), skipBackPropagation ? previousNode : null);
        this.messagesPropagated += 1;
    }
    inspect(peerDescriptor) {
        return this.options.inspector.inspect(peerDescriptor);
    }
    getPropagationTargets(msg) {
        let propagationTargets = this.options.neighbors.getIds();
        if (this.options.proxyConnectionRpcLocal) {
            propagationTargets = propagationTargets.concat(this.options.proxyConnectionRpcLocal.getPropagationTargets(msg));
        }
        propagationTargets = propagationTargets.concat(this.options.temporaryConnectionRpcLocal.getNodes().getIds());
        return propagationTargets;
    }
    getOwnNodeId() {
        return (0, dht_1.toNodeId)(this.options.localPeerDescriptor);
    }
    getOutgoingHandshakeCount() {
        return this.options.handshaker.getOngoingHandshakes().size;
    }
    getNeighbors() {
        if (!this.started && this.isStopped()) {
            return [];
        }
        return this.options.neighbors.getAll().map((n) => n.getPeerDescriptor());
    }
    getInfos() {
        return this.options.neighbors.getAll().map((n) => {
            return {
                peerDescriptor: n.getPeerDescriptor(),
                rtt: n.getRtt()
            };
        });
    }
    getNearbyNodeView() {
        return this.options.nearbyNodeView;
    }
    getDiagnosticInfo() {
        return {
            neighborCount: this.options.neighbors.size(),
            nearbyNodeViewCount: this.options.nearbyNodeView.size(),
            randomNodeViewCount: this.options.randomNodeView.size(),
            leftNodeViewCount: this.options.leftNodeView.size(),
            rightNodeViewCount: this.options.rightNodeView.size(),
            messagesPropagated: this.messagesPropagated
        };
    }
    isStopped() {
        return this.abortController.signal.aborted;
    }
}
exports.ContentDeliveryLayerNode = ContentDeliveryLayerNode;
//# sourceMappingURL=ContentDeliveryLayerNode.js.map